import { onMounted, onBeforeUnmount, watch } from 'vue'
import { publicApi, adminApi } from '../lib/api.js'
import { useAuthStore } from '../stores/auth.js'
import { usePresenceStore } from '../stores/presence.js'

const HEARTBEAT_MS = 60000

/**
 * Heartbeat app-wide per la presence (utenti connessi). Montato una sola volta
 * (in App.vue). Ogni ~60s pinga l'endpoint adatto al contesto corrente:
 *   - sessione admin autenticata → adminApi.presence.ping → { app, admin }
 *   - resto (visitatori pubblici) → publicApi.presence.ping → { online }
 * e aggiorna lo store. La scelta si basa su `auth.authenticated` (non sulla
 * route) così un login/logout ri-pinga subito senza attendere il tick
 * successivo. In pausa quando la tab è nascosta (Page Visibility), come
 * useSmartPoll. Best-effort: un errore non deve mai disturbare la UI.
 */
export function usePresence () {
  const auth = useAuthStore()
  const store = usePresenceStore()
  let interval = null

  async function beat () {
    try {
      if (auth.authenticated) {
        store.setFromAdmin(await adminApi.presence.ping())
      } else {
        store.setFromPublic(await publicApi.presence.ping())
      }
    } catch { /* presence best-effort */ }
  }

  function start () {
    if (interval) return
    beat()
    interval = setInterval(beat, HEARTBEAT_MS)
  }
  function stop () {
    if (interval) { clearInterval(interval); interval = null }
  }

  function onVisibility () {
    if (document.visibilityState === 'hidden') stop()
    else start()
  }

  // Login/logout (o esito di checkSession al primo load admin) → ri-pinga
  // subito con l'endpoint giusto, senza aspettare il tick dei 60s.
  watch(() => auth.authenticated, () => { if (interval) beat() })

  onMounted(() => {
    document.addEventListener('visibilitychange', onVisibility)
    if (document.visibilityState !== 'hidden') start()
  })
  onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', onVisibility)
    stop()
  })
}
